import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../common/SharedStyles.css';
import './LearnPage.css';
import PageContainer from '../common/PageContainer';
import WordInfo from '../common/WordInfo';

interface VocabularyEntry {
  _id: string;
  word: string;
  definition: string;
  definitions?: {
    text: string;
    partOfSpeech?: string;
    examples: string[];
  }[];
  examples: string[];
  searchCount: number;
  lastSearched: Date | string;
  source?: 'openai' | 'fallback';
  phonetics?: string;
  phonics?: string;
}

interface GeneratedExample {
  word?: string;
  words?: string[];
  example: string;
  createdAt: Date;
}

interface WordDefinition {
  word: string;
  definition: string;
  definitions?: {
    text: string;
    partOfSpeech?: string;
    examples: string[];
  }[];
  phonetics?: string;
  phonics?: string;
  examples?: string[];
  source?: string;
  loading?: boolean;
}

const LearnPage = () => {
  const [vocabulary, setVocabulary] = useState<VocabularyEntry[]>([]);
  const [generating, setGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [example, setExample] = useState<GeneratedExample | null>(null);
  const [selectedWord, setSelectedWord] = useState<WordDefinition | null>(null);
  const [clickedWords, setClickedWords] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    loadVocabulary();
  }, []);
  
  const loadVocabulary = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in to use the learn feature');
      return;
    }
    
    try {
      console.log('Loading vocabulary data...');
      const response = await fetch('/api/search/vocabulary', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const vocabEntries = await response.json() as VocabularyEntry[];
        console.log(`Loaded ${vocabEntries.length} vocabulary entries`);
        setVocabulary(vocabEntries);
        setError(''); // Clear any previous errors
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.message || 'Failed to load your vocabulary';
        console.error(`Failed to load vocabulary: ${errorMessage}`);
        setError(errorMessage);
      }
    } catch (error) {
      setError('An error occurred while loading your vocabulary');
      console.error('Error loading vocabulary:', error);
    }
  };
  
  const generateOneWordExample = async () => {
    if (vocabulary.length === 0) {
      setError('You need to have vocabulary words to generate examples');
      return;
    }
    
    setGenerating(true);
    setError('');
    setSelectedWord(null); // Clear any selected word definition
    setClickedWords(new Set()); // Reset clicked words when generating new example
    
    try {
      // Select a random word from vocabulary
      const randomIndex = Math.floor(Math.random() * vocabulary.length);
      const selectedWord = vocabulary[randomIndex];
      
      const token = localStorage.getItem('token');
      const response = await fetch('/api/learn/generate-example', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          word: selectedWord.word
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setExample({
          word: selectedWord.word,
          example: data.example,
          createdAt: new Date()
        });
        
        // Pre-highlight the target word
        setClickedWords(new Set([selectedWord.word.toLowerCase()]));
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to generate example');
      }
    } catch (error) {
      console.error('Error generating example:', error);
      setError('An error occurred while generating the example');
    } finally {
      setGenerating(false);
    }
  };
  
  const generateMultipleWordsExample = async () => {
    if (vocabulary.length < 3) {
      setError('You need at least 3 vocabulary words to generate a paragraph');
      return;
    }
    
    setGenerating(true);
    setError('');
    setSelectedWord(null); // Clear any selected word definition
    setClickedWords(new Set()); // Reset clicked words when generating new example
    
    try {
      // Select 10 random words (or fewer if vocabulary is smaller)
      const shuffled = [...vocabulary].sort(() => 0.5 - Math.random());
      const selectedWords = shuffled.slice(0, Math.min(10, vocabulary.length));
      const wordList = selectedWords.map(entry => entry.word);
      
      const token = localStorage.getItem('token');
      const response = await fetch('/api/learn/generate-paragraph', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          words: wordList
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setExample({
          words: data.usedWords,
          example: data.paragraph,
          createdAt: new Date()
        });
        
        // Pre-highlight the target words
        if (data.usedWords && data.usedWords.length > 0) {
          setClickedWords(new Set(data.usedWords.map((w: string) => w.toLowerCase())));
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to generate paragraph');
      }
    } catch (error) {
      console.error('Error generating paragraph:', error);
      setError('An error occurred while generating the paragraph');
    } finally {
      setGenerating(false);
    }
  };
  
  const handleBackToSearch = () => {
    navigate('/');
  };
  
  const formatTimestamp = (date: Date) => {
    return new Date(date).toLocaleTimeString();
  };
  
  // Add a function to handle refreshing definition
  const handleRefreshDefinition = async () => {
    if (!selectedWord || !selectedWord.word || refreshing) return;
    
    setRefreshing(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token available');
        setRefreshing(false);
        return;
      }
      
      // Force fetch a new definition from OpenAI
      const response = await fetch(`/api/search/define?word=${encodeURIComponent(selectedWord.word)}&forceApi=true`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to get updated definition');
      }
      
      const data = await response.json();
      
      // Now save the updated definition to the database
      const saveResponse = await fetch('/api/search/history', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          word: data.word,
          definitions: data.definitions,
          phonetics: data.phonetics,
          phonics: data.phonics,
          source: data.source
        })
      });
      
      if (!saveResponse.ok) {
        throw new Error('Failed to save updated definition');
      }
      
      // Update the selected word with the new definition
      setSelectedWord({
        word: data.word,
        definition: data.definition,
        definitions: data.definitions,
        phonetics: data.phonetics,
        phonics: data.phonics,
        examples: data.examples,
        source: data.source
      });
      
      // Also update the vocabulary list
      const savedData = await saveResponse.json();
      if (savedData && savedData.vocabularyEntry) {
        setVocabulary(prevVocab => {
          // Find and update the word in vocabulary
          return prevVocab.map(entry => 
            entry.word.toLowerCase() === data.word.toLowerCase()
              ? {
                  ...entry,
                  definition: data.definition,
                  examples: data.examples || [],
                  phonetics: data.phonetics || '',
                  phonics: data.phonics || '',
                  source: data.source || 'openai'
                }
              : entry
          );
        });
      }
    } catch (error) {
      console.error('Error refreshing definition:', error);
      alert('Failed to refresh definition. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };
  
  // Function to fetch word definition
  const fetchWordDefinition = async (word: string) => {
    console.log(`Fetching definition for: "${word}"`);
    
    // First, check if the word is in vocabulary
    const vocabWord = vocabulary.find(entry => 
      entry.word.toLowerCase() === word.toLowerCase()
    );
    
    console.log(`Vocabulary lookup result:`, vocabWord ? 
      { id: vocabWord._id, word: vocabWord.word } : 
      "Not found in vocabulary");
    
    // Check if the word has a valid definition in vocabulary
    const hasValidDefinition = vocabWord && 
      vocabWord.definition && 
      !vocabWord.definition.includes("not available") &&
      !vocabWord.definition.includes("No definition available");
    
    if (hasValidDefinition) {
      console.log(`Using existing definition for "${word}" from vocabulary`);
      setSelectedWord({
        word: vocabWord!.word,
        definition: vocabWord!.definition,
        definitions: vocabWord.definitions,
        phonetics: vocabWord!.phonetics,
        phonics: vocabWord!.phonics,
        examples: vocabWord!.examples,
        source: vocabWord!.source
      });
      return;
    }
    
    // If not in vocabulary or definition not available, get definition from API
    console.log(`Fetching definition for "${word}" from API`);
    setSelectedWord({
      word,
      definition: '',
      loading: true
    });
    
    const token = localStorage.getItem('token');
    if (!token) {
      console.error("No token available for API request");
      setSelectedWord({
        word,
        definition: 'Please log in to view definitions',
        source: 'error'
      });
      return;
    }
    
    try {
      // First try to get the definition without forcing API call
      const response = await fetch(`/api/search/define?word=${encodeURIComponent(word)}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`Definition API response for "${word}":`, data);
        
        // Check if we got a fallback definition with "not available" or empty definition
        const needsAiLookup = 
          (data.source === 'fallback' && 
           (data.definition.includes("not available") || 
            data.definition.includes("No definition available"))) ||
          !data.definition;
        
        if (needsAiLookup) {
          // Make an API request for AI-generated definition - forcing OpenAI lookup
          try {
            console.log(`Fallback definition not sufficient, forcing API lookup for "${word}"`);
            const forceApiParam = "forceApi=true";
            const aiResponse = await fetch(`/api/search/define?word=${encodeURIComponent(word)}&${forceApiParam}`, {
              headers: {
                Authorization: `Bearer ${token}`
              }
            });
            
            if (aiResponse.ok) {
              const aiData = await aiResponse.json();
              console.log(`AI definition response for "${word}":`, aiData);
              
              setSelectedWord({
                word: aiData.word || word,
                definition: aiData.definition,
                definitions: aiData.definitions,
                phonetics: aiData.phonetics,
                phonics: aiData.phonics,
                examples: aiData.examples,
                source: 'openai'
              });
              
              // Add to vocabulary by saving to history (regardless of whether the word exists)
              await saveToVocabulary(aiData.word || word, aiData);
            } else {
              setSelectedWord({
                word,
                definition: 'Failed to fetch AI definition',
                source: 'error'
              });
            }
          } catch (aiError) {
            console.error('Error fetching AI definition:', aiError);
            setSelectedWord({
              word,
              definition: 'Error fetching AI definition',
              source: 'error'
            });
          }
        } else {
          // Use the definition from the response
          console.log(`Using definition from standard API response for "${word}"`);
          setSelectedWord({
            word: data.word,
            definition: data.definition,
            definitions: data.definitions,
            phonetics: data.phonetics,
            phonics: data.phonics,
            examples: data.examples,
            source: data.source
          });
          
          // Add to vocabulary if it doesn't exist
          if (!vocabWord) {
            await saveToVocabulary(data.word, data);
          }
        }
      } else {
        console.error(`API error fetching definition for "${word}":`, response.status);
        setSelectedWord({
          word,
          definition: 'Failed to fetch definition',
          source: 'error'
        });
      }
    } catch (error) {
      console.error('Error fetching definition:', error);
      setSelectedWord({
        word,
        definition: 'Error fetching definition',
        source: 'error'
      });
    }
  };
  
  // Helper function to save a word to the user's vocabulary
  const saveToVocabulary = async (word: string, data: any) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      console.log(`Saving word to vocabulary: "${word}"`);
      
      // Make sure we have all required fields
      const requestBody = {
        word: word,
        definitions: data.definitions || [{ text: data.definition || '', examples: data.examples || [] }],
        phonetics: data.phonetics || '',
        phonics: data.phonics || '',
        source: data.source || 'openai'
      };
      
      const response = await fetch('/api/search/history', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });
      
      if (response.ok) {
        console.log(`Added "${word}" to vocabulary`);
        
        // Instead of reloading all vocabulary, just add this word to the state
        const savedData = await response.json();
        console.log("Save response:", savedData);
        
        if (savedData && savedData.vocabularyEntry) {
          // Update vocabulary state by adding the new entry
          setVocabulary(prevVocab => {
            // Check if the word already exists in vocabulary
            const existingIndex = prevVocab.findIndex(
              entry => entry.word.toLowerCase() === word.toLowerCase()
            );
            
            if (existingIndex >= 0) {
              // Word exists, update it
              console.log(`Updating existing word "${word}" in vocabulary state`);
              const updatedVocab = [...prevVocab];
              updatedVocab[existingIndex] = {
                ...updatedVocab[existingIndex],
                definition: data.definition,
                definitions: data.definitions,
                examples: data.examples || [],
                phonetics: data.phonetics || '',
                phonics: data.phonics || '',
                source: data.source || 'openai'
              };
              return updatedVocab;
            } else {
              // Word doesn't exist, add it
              console.log(`Adding new word "${word}" to vocabulary state`);
              const newEntry = {
                _id: savedData.vocabularyEntry._id,
                word: word,
                definition: data.definition,
                definitions: data.definitions,
                examples: data.examples || [],
                phonetics: data.phonetics || '',
                phonics: data.phonics || '',
                source: data.source || 'openai',
                searchCount: 1,
                lastSearched: new Date().toISOString()
              };
              console.log("New vocabulary entry:", newEntry);
              return [...prevVocab, newEntry];
            }
          });
        } else {
          console.warn("Saved data response didn't include vocabularyEntry:", savedData);
        }
      } else {
        const errorData = await response.json();
        console.error(`Failed to add "${word}" to vocabulary: ${JSON.stringify(errorData)}`);
      }
    } catch (saveError) {
      console.error('Error saving word to vocabulary:', saveError);
    }
  };
  
  // Function to handle word click
  const handleWordClick = (word: string) => {
    // Check if this is a multi-word phrase like "put back"
    const isPhrasalVerb = word.includes(' ');
    
    if (isPhrasalVerb) {
      // For phrasal verbs, use the whole phrase as is
      const cleanPhrase = word.toLowerCase();
      
      // Add this phrase to clicked words set
      setClickedWords(prev => {
        const newSet = new Set(prev);
        newSet.add(cleanPhrase);
        return newSet;
      });
      
      if (selectedWord && selectedWord.word.toLowerCase() === cleanPhrase) {
        // Toggle off if clicking the same word
        setSelectedWord(null);
      } else {
        fetchWordDefinition(cleanPhrase);
      }
      
      return;
    }
    
    // For single words, keep the current behavior
    // Clean up the word - remove punctuation
    const cleanWord = word.replace(/[.,;:!?'"()]/g, '').toLowerCase();
    
    // Skip empty words and single-character words (likely punctuation)
    if (!cleanWord || cleanWord.length <= 1) return;
    
    // Add this word to clicked words set
    setClickedWords(prev => {
      const newSet = new Set(prev);
      newSet.add(cleanWord);
      return newSet;
    });
    
    if (selectedWord && selectedWord.word.toLowerCase() === cleanWord) {
      // Toggle off if clicking the same word
      setSelectedWord(null);
    } else {
      fetchWordDefinition(cleanWord);
    }
  };
  
  // Function to process text and make every word clickable
  const processText = (text: string, initiallyHighlightedWords: string[] = []) => {
    if (!text) return text;
    
    // Create a list of all phrases to look for
    // Including phrasal verbs and multi-word expressions from the vocabulary and initiallyHighlightedWords
    const allPhrases = [
      ...vocabulary.map(entry => entry.word),
      ...initiallyHighlightedWords
    ];
    
    // Sort phrases by length (longest first) to prioritize longer matches like phrasal verbs
    const sortedPhrases = allPhrases
      .filter(word => word.includes(' ')) // Only include multi-word phrases
      .sort((a, b) => b.length - a.length);
    
    // First, replace phrasal verbs with a special marker
    let processedText = text;
    const phrasalVerbMarkers: {[key: string]: string} = {};
    let markerCounter = 0;
    
    // Replace all phrasal verbs with markers
    sortedPhrases.forEach(phrase => {
      // Case insensitive replacement
      const phraseRegex = new RegExp(phrase.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'gi');
      if (phraseRegex.test(processedText)) {
        const marker = `__PHRASAL_VERB_${markerCounter}__`;
        phrasalVerbMarkers[marker] = phrase;
        processedText = processedText.replace(phraseRegex, marker);
        markerCounter++;
      }
    });
    
    // Regular expression to split text into words and non-words (spaces, punctuation)
    const wordRegex = /(\w+|__PHRASAL_VERB_\d+__|[^\w\s]+|\s+)/g;
    const parts = processedText.match(wordRegex) || [];
    
    return parts.map((part, index) => {
      // Check if part is a phrasal verb marker
      if (part.startsWith('__PHRASAL_VERB_')) {
        const phraseWord = phrasalVerbMarkers[part];
        const cleanWord = phraseWord.toLowerCase();
        const isHighlighted = clickedWords.has(cleanWord) || 
                             initiallyHighlightedWords.some(w => 
                               w.toLowerCase() === cleanWord
                             );
        
        return (
          <span 
            key={index} 
            className={isHighlighted ? "highlighted-word-in-text clickable" : "clickable-word"}
            onClick={() => handleWordClick(phraseWord)}
          >
            {phraseWord}
          </span>
        );
      }
      
      // Check if part is a word (not spaces or punctuation)
      const isWord = /^\w+$/.test(part);
      
      if (!isWord) {
        // Return non-word parts as-is
        return part;
      }
      
      // Check if this word should be highlighted
      const cleanWord = part.toLowerCase();
      const isHighlighted = clickedWords.has(cleanWord) || 
                           initiallyHighlightedWords.some(w => 
                             w.toLowerCase() === cleanWord
                           );
      
      return (
        <span 
          key={index} 
          className={isHighlighted ? "highlighted-word-in-text clickable" : "clickable-word"}
          onClick={() => handleWordClick(part)}
        >
          {part}
        </span>
      );
    });
  };
  
  // Function to delete a word from vocabulary
  const deleteWordFromVocabulary = async (word: string) => {
    console.log(`Attempting to delete word: "${word}"`);
    console.log(`Current vocabulary state:`, vocabulary.map(v => ({ id: v._id, word: v.word })));
    
    // First find the word in vocabulary to get its _id
    const vocabEntry = vocabulary.find(entry => 
      entry.word.toLowerCase() === word.toLowerCase()
    );
    
    if (!vocabEntry || !vocabEntry._id) {
      console.error(`Could not find vocabulary entry for "${word}"`);
      
      // If we have a selectedWord but it's not in vocabulary yet, clear it
      if (selectedWord && selectedWord.word.toLowerCase() === word.toLowerCase()) {
        console.log("Word not in vocabulary yet, just clearing the selected word");
        setSelectedWord(null);
        return;
      }
      
      setError(`Could not find vocabulary entry for "${word}". Try refreshing the page.`);
      return;
    }
    
    console.log(`Found word entry with ID: ${vocabEntry._id}`);
    
    const wordId = vocabEntry._id;
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No authentication token available');
      setError('Please log in to remove words');
      return;
    }
    
    console.log(`Making DELETE request to /api/search/vocabulary/${wordId}`);
    
    try {
      const response = await fetch(`/api/search/vocabulary/${wordId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log(`Delete response status: ${response.status}`);
      
      if (response.ok) {
        console.log(`Word "${word}" deleted successfully`);
        
        // Word deleted successfully, update UI
        setVocabulary(prevVocab => {
          console.log("Filtering vocabulary:", prevVocab.length, "entries");
          return prevVocab.filter(entry => entry._id !== wordId);
        });
        
        // Clear selected word if it's the one that was deleted
        if (selectedWord && selectedWord.word.toLowerCase() === word.toLowerCase()) {
          console.log("Clearing selected word");
          setSelectedWord(null);
        }
        
        // Also update clickedWords set
        setClickedWords(prev => {
          const newSet = new Set(prev);
          newSet.delete(word.toLowerCase());
          return newSet;
        });
        
        // Show success message
        setError(''); // Clear any previous errors
      } else {
        const data = await response.json();
        const errorMessage = data.message || `Failed to delete "${word}" from vocabulary`;
        console.error(`Error response: ${JSON.stringify(data)}`);
        setError(errorMessage);
      }
    } catch (error) {
      console.error('Error deleting word from vocabulary:', error);
      setError('An error occurred while removing the word. Check console for details.');
    }
  };
  
  return (
    <PageContainer>
      <div className="page-container">
        <div className="page-header">
          <h1>Learn With Your Words</h1>
          <button className="primary-button" onClick={handleBackToSearch}>
            Back to Search
          </button>
        </div>
        
        {error && (
          <div className="error-message">
            {error}
            <button 
              className="refresh-button-subtle" 
              onClick={loadVocabulary}
              style={{ marginLeft: '10px' }}
            >
              Reload Data
            </button>
          </div>
        )}
        
        {vocabulary.length === 0 && !error ? (
          <div className="empty-state">
            <p>You need to search for words before you can use the learn feature.</p>
            <button className="primary-button" onClick={handleBackToSearch}>
              Search Now
            </button>
          </div>
        ) : (
          <div className="learn-container">
            <div className="learn-actions">
              <button 
                className="learn-button"
                onClick={generateOneWordExample}
                disabled={generating}
              >
                One Word Example
              </button>
              <button 
                className="learn-button multi-word"
                onClick={generateMultipleWordsExample}
                disabled={generating}
              >
                Multiple Words Paragraph
              </button>
            </div>
            
            {generating ? (
              <div className="loading-spinner">Generating example...</div>
            ) : example ? (
              <div className="example-container">
                {example.word ? (
                  <div className="example-header">
                    <h3>Example for: <span className="highlighted-word">{example.word}</span></h3>
                    <span className="example-time">Generated at {formatTimestamp(example.createdAt)}</span>
                  </div>
                ) : example.words ? (
                  <div className="example-header">
                    <h3>Paragraph using these words:</h3>
                    <div className="used-words">
                      {example.words.map((word, index) => (
                        <span 
                          key={index} 
                          className="highlighted-word clickable"
                          onClick={() => handleWordClick(word)}
                        >
                          {word}
                        </span>
                      ))}
                    </div>
                    <span className="example-time">Generated at {formatTimestamp(example.createdAt)}</span>
                  </div>
                ) : null}
                
                <div className="example-content">
                  {processText(example.example, example.words || (example.word ? [example.word] : []))}
                </div>
                
                {selectedWord && (
                  <div className="word-definition-container">
                    <div className="definition-header-container">
                      <h4 className="definition-header">{selectedWord.word}</h4>
                      <button 
                        className="remove-button"
                        onClick={() => deleteWordFromVocabulary(selectedWord.word)}
                        title="Remove word"
                      >
                        Remove
                      </button>
                    </div>
                    
                    {selectedWord.loading ? (
                      <div className="loading-spinner">Loading definition...</div>
                    ) : (
                      <WordInfo
                        definition={selectedWord.definition}
                        definitions={selectedWord.definitions}
                        phonetics={selectedWord.phonetics}
                        phonics={selectedWord.phonics}
                        examples={selectedWord.examples}
                        source={selectedWord.source}
                        onRefresh={handleRefreshDefinition}
                        refreshing={refreshing}
                      />
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="instruction-panel">
                <p>Click one of the buttons above to generate an example using your vocabulary words.</p>
                <p>
                  <strong>One Word Example:</strong> Generates a sentence using a random word from your vocabulary.
                </p>
                <p>
                  <strong>Multiple Words Paragraph:</strong> Creates a short paragraph using several words from your vocabulary.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default LearnPage; 